import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Kebab-menu triage actions on the Messages email detail sidebar (draft reply,
// star, archive, move-to-inbox, trash, mark spam). Reuses the same JSON
// endpoints as row-swipe and card-stack. The chat and person panels use only
// archive: they have no message id, so `row` names their list row instead.
export default class extends Controller {
    static values = {
        messageId: String,
        row: String,
        draftUrl: String,
        starUrl: String,
        unstarUrl: String,
        archiveUrl: String,
        unarchiveUrl: String,
        trashUrl: String,
        spamUrl: String,
        draftStatusUrl: String
    }

    static targets = ["starLabel", "draftLabel"]

    DRAFT_POLL_MS = 3000
    DRAFT_TIMEOUT_MS = 120000

    connect() {
        this._starred = false
        this._draftPollTimer = null
        this._draftStartedAt = null
    }

    disconnect() {
        if (this._draftPollTimer) clearTimeout(this._draftPollTimer)
    }

    draft(event) {
        event.preventDefault()
        if (this._draftBusy) return
        this._draftBusy = true
        this._setDraftLabel("Drafting…")

        fetch(this.draftUrlValue, {
            method: "POST",
            headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((data) => {
                if (data.status === "ready") {
                    this._setDraftLabel("Draft ready")
                    this._setRowBadge("Draft ready", "is-ready")
                } else {
                    this._draftStartedAt = Date.now()
                    this._setRowBadge("Drafting…", "is-drafting")
                    this._scheduleDraftPoll()
                }
            })
            .catch(() => {
                this._setDraftLabel("Draft failed")
                this._setRowBadge("Draft failed", "is-failed")
            })
            .finally(() => {
                this._draftBusy = false
            })
    }

    star(event) {
        event.preventDefault()
        if (this._starBusy) return
        this._starBusy = true
        let starring = !this._starred
        let url = starring ? this.starUrlValue : this.unstarUrlValue

        fetch(url, {
            method: "POST",
            headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(() => {
                this._starred = starring
                this._updateStarLabel()
            })
            .catch(() => {})
            .finally(() => {
                this._starBusy = false
            })
    }

    archive(event) {
        this._removeAndAdvance(event, this.archiveUrlValue)
    }

    // Move an archived thread back to the inbox. Removes it from the current
    // (archived) view and advances to the next thread, same as archive.
    moveToInbox(event) {
        this._removeAndAdvance(event, this.unarchiveUrlValue)
    }

    trash(event) {
        this._removeAndAdvance(event, this.trashUrlValue)
    }

    spam(event) {
        this._removeAndAdvance(event, this.spamUrlValue)
    }

    // Shared optimistic "remove the row from the list + move on" flow used by
    // archive / move-to-inbox / trash / mark-spam: all four take the thread
    // out of the current list, and the panel then advances to the next thread
    // so triage keeps flowing. On failure the row is restored.
    _removeAndAdvance(event, url) {
        event.preventDefault()
        if (!url || this._removeBusy) return
        this._removeBusy = true
        let row = this._row()
        let unit = row ? (row.closest(".msg-swipe") || row) : null

        if (unit) {
            unit.classList.add("is-leaving")
            if (row) row.style.transform = ""
        }

        fetch(url, {
            method: "POST",
            headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(() => {
                if (unit) this._collapseUnit(unit)
                this._advanceSidebar(row)
            })
            .catch(() => {
                if (unit) {
                    unit.classList.remove("is-leaving")
                    if (row) {
                        row.classList.add("is-action-failed")
                        setTimeout(() => row.classList.remove("is-action-failed"), 1200)
                    }
                }
            })
            .finally(() => {
                this._removeBusy = false
            })
    }

    _scheduleDraftPoll() {
        if (this._draftPollTimer || !this.hasDraftStatusUrlValue) return
        this._draftPollTimer = setTimeout(() => {
            this._draftPollTimer = null
            this._pollDraftStatus()
        }, this.DRAFT_POLL_MS)
    }

    _pollDraftStatus() {
        if (!this.hasDraftStatusUrlValue) return
        fetch(`${this.draftStatusUrlValue}?ids[]=${encodeURIComponent(this.messageIdValue)}`, {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((data) => {
                let ready = (data.ready || []).map(String)
                if (ready.includes(String(this.messageIdValue))) {
                    this._setDraftLabel("Draft ready")
                    this._setRowBadge("Draft ready", "is-ready")
                    return
                }
                if (this._draftStartedAt && Date.now() - this._draftStartedAt >= this.DRAFT_TIMEOUT_MS) {
                    this._setDraftLabel("Draft failed")
                    this._setRowBadge("Draft failed", "is-failed")
                    return
                }
                this._scheduleDraftPoll()
            })
            .catch(() => this._scheduleDraftPoll())
    }

    _setDraftLabel(text) {
        if (this.hasDraftLabelTarget) this.draftLabelTarget.textContent = text
    }

    _updateStarLabel() {
        if (!this.hasStarLabelTarget) return
        this.starLabelTarget.textContent = this._starred ? "Unstar" : "Star"
    }

    _row() {
        if (this.hasRowValue) return document.querySelector(this.rowValue)
        if (!this.hasMessageIdValue) return null
        return document.querySelector(`.assistant-thread-item[data-message-id="${CSS.escape(this.messageIdValue)}"]`)
    }

    _setRowBadge(text, state) {
        let row = this._row()
        if (!row) return
        let badge = row.querySelector(".msg-row-badge")
        if (!badge) {
            badge = document.createElement("span")
            badge.className = "msg-row-badge"
            let time = row.querySelector(".msg-row-time")
            if (time) {
                time.parentNode.insertBefore(badge, time)
            } else {
                row.appendChild(badge)
            }
        }
        badge.textContent = text
        badge.classList.remove("is-drafting", "is-ready", "is-failed")
        badge.classList.add(state)
    }

    _collapseUnit(unit) {
        unit.style.height = `${unit.offsetHeight}px`
        void unit.offsetHeight
        unit.classList.add("is-collapsed")
        unit.style.height = "0px"
        setTimeout(() => unit.remove(), 240)
    }

    // Hand the just-removed row to msg-detail so the panel opens the next
    // thread in the list instead of closing. Older markup without the advance
    // hook (and an empty list) still just closes.
    _advanceSidebar(row) {
        let ctrl = this._detailController()
        if (!ctrl) return
        if (typeof ctrl.advanceAfterRemoval === "function") ctrl.advanceAfterRemoval(row)
        else if (typeof ctrl.close === "function") ctrl.close()
    }

    _detailController() {
        let list = document.querySelector(".assistant-thread-list[data-controller*='msg-detail']")
        if (!list) return null
        return this.application.getControllerForElementAndIdentifier(list, "msg-detail")
    }

}
